import { CustomSpecFieldDef } from '../constants/machineryCustomSpecFieldsByType';
import { IMachineryDraft } from '../interfaces/heavyDutyMachinery/IMachineryAssist';

/**
 * Turning what the publishing assistant suggested into values the wizard's
 * form store understands -- and remembering which ones came from it.
 *
 * Lives out here rather than inside HeavyDutyMachineryForm because two things
 * here are worth being able to test on their own: that a suggestion NEVER
 * overwrites something the host typed, and that a per-type spec arriving as
 * the string "1.2" becomes the number 1.2 the numeric input expects.
 */

/**
 * Whether the form store holds nothing for a field.
 *
 * The one place "has the host answered this?" is decided, used both for
 * "don't overwrite what they typed" below and for finding the first phase
 * with a hole left in it. `false` counts as ANSWERED -- a switch deliberately
 * turned off is an answer, and treating it as blank is how an assistant
 * silently turns somebody's quick hitch back on.
 */
export const isBlankFormValue = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

/**
 * The fields whose suggestion carries a consequence somebody else pays for.
 *
 * Weight and the three transport dimensions are what a guest books a
 * low-loader and a permit against. Everything else the assistant fills is
 * wrong on a screen; these are wrong at a bridge, so they are marked
 * differently -- see AiFilledMark's coral tone.
 */
export const AI_CHECK_FIELDS = ['weightTons', 'transportLengthM', 'transportWidthM', 'transportHeightM'];

/** What the draft turned into, ready to be handed to form.setFieldsValue. */
export interface MachineryDraftApplication {
  /** Plain top-level form fields. Already excludes anything the host had answered. */
  values: Record<string, unknown>;
  /** The per-type specs, cast to what each field's own input expects. Merged onto whatever is already there, never replacing the object. */
  customSpecs: Record<string, string | number | boolean>;
  /**
   * Every field name this draft actually set, custom specs included as
   * `customSpecs.<key>`. It is what the screen marks with a sparkle, so it
   * lists what the host has to check -- and nothing they wrote themselves.
   */
  filledFields: string[];
}

/** One per-type spec, cast from the string the wire carries into what its own input expects. Undefined means "drop it". */
const castCustomSpec = (field: CustomSpecFieldDef, raw: string): string | number | boolean | undefined => {
  const value = (raw ?? '').trim();
  if (value === '') return undefined;

  if (field.type === 'number') {
    // The server already normalises the decimal separator, so this is the
    // second line of defence rather than the first.
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  if (field.type === 'boolean') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
    return undefined;
  }

  if (field.type === 'select') {
    // A select whose value is not one of its own options renders as empty,
    // which looks like the assistant failed rather than like it was filtered.
    return (field.options ?? []).some((option) => option.value === value) ? value : undefined;
  }

  return value;
};

/**
 * The draft as form values, with everything the host already answered left
 * exactly as it was.
 *
 * **Not overwriting is the rule, not an optimisation.** Somebody who typed a
 * weight and then pressed the button is telling us their number is better
 * than ours -- it is their machine. This is also what makes the whole feature
 * safe to press twice.
 *
 * @param current the form store as it stands, straight from getFieldsValue(true).
 */
export const applyMachineryDraft = (
  draft: IMachineryDraft,
  customSpecFields: CustomSpecFieldDef[],
  current: Record<string, unknown>
): MachineryDraftApplication => {
  const values: Record<string, unknown> = {};
  const filledFields: string[] = [];

  const offer = (field: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    if (!isBlankFormValue(current[field])) return;
    values[field] = value;
    filledFields.push(field);
  };

  offer('title', draft.title);
  offer('description', draft.description);
  offer('manufacturerBrand', draft.manufacturerBrand);
  offer('modelName', draft.modelName);
  offer('weightTons', draft.weightTons);
  offer('powerHp', draft.powerHp);
  offer('reachMeters', draft.reachMeters);
  offer('propulsionType', draft.propulsionType);
  offer('fuelType', draft.fuelType);
  offer('transportLengthM', draft.transportLengthM);
  offer('transportWidthM', draft.transportWidthM);
  offer('transportHeightM', draft.transportHeightM);
  offer('hasQuickHitch', draft.hasQuickHitch);

  const schema = new Map(customSpecFields.map((field) => [field.key, field]));
  const existingSpecs = (current.customSpecs ?? {}) as Record<string, string | number | boolean>;
  const customSpecs: Record<string, string | number | boolean> = {};

  for (const attribute of draft.customAttributes ?? []) {
    const field = schema.get(attribute.key);
    // Already filtered server-side against this very schema; checked again
    // because the type it has to be cast to lives here, and a key with no
    // field definition has no type to cast to.
    if (!field) continue;
    if (!isBlankFormValue(existingSpecs[attribute.key])) continue;

    const cast = castCustomSpec(field, attribute.value);
    if (cast === undefined) continue;

    customSpecs[attribute.key] = cast;
    filledFields.push(`customSpecs.${attribute.key}`);
  }

  return { values, customSpecs, filledFields };
};

/**
 * What a photograph of the identification plate is allowed to put in the form.
 *
 * Governed by the same rule as everything else the assistant proposes -- it
 * never replaces an answer the host has already given -- but worth its own
 * function because what it carries is different in kind: these four are facts
 * about ONE physical machine, read off metal, rather than facts about a model.
 * Nothing here was inferred from anything, which is exactly why the server
 * leaves a field empty rather than half-read.
 */
export const applyPlateRead = (
  plate: { manufacturerBrand?: string; modelName?: string; serialNumber?: string; yearManufactured?: number },
  current: Record<string, unknown>
): MachineryDraftApplication => {
  const values: Record<string, unknown> = {};
  const filledFields: string[] = [];

  const offer = (field: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    if (!isBlankFormValue(current[field])) return;
    values[field] = value;
    filledFields.push(field);
  };

  offer('manufacturerBrand', plate.manufacturerBrand);
  offer('modelName', plate.modelName);
  offer('serialNumber', plate.serialNumber);
  offer('yearManufactured', plate.yearManufactured);

  return { values, customSpecs: {}, filledFields };
};
